import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { LOGO_URL, CONTACT } from "@/data";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Community", href: "#community" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled
          ? "bg-[#0C1827]/90 backdrop-blur-xl border-[#1E324A] shadow-lg shadow-black/30"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#home" className="flex items-center gap-3" data-testid="header-logo">
            <img
              src={LOGO_URL}
              alt="Almaguin Yard Solutions logo"
              className="h-14 w-14 rounded-full object-cover ring-2 ring-[#F5A623]/70 bg-white"
            />
            <div className="hidden sm:block leading-tight">
              <span className="font-display font-extrabold text-white tracking-tight text-sm uppercase block">
                Almaguin Yard Solutions
              </span>
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#F5A623]">
                Precision. Performance. Perfection.
              </span>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`header-nav-${link.label.toLowerCase()}`}
                className="text-sm font-medium text-slate-300 hover:text-[#F5A623] transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#quote"
              data-testid="header-nav-quote-link"
              className="text-sm font-semibold bg-[#F5A623] text-[#0C1827] px-5 py-2.5 rounded-full hover:bg-[#E8961F] hover:-translate-y-0.5 transition-[background-color,transform] duration-200"
            >
              Get a Quote
            </a>
            <a
              href={CONTACT.phoneHref}
              data-testid="header-phone-button"
              className="flex items-center gap-2 text-sm font-semibold text-white border border-[#F5A623]/50 px-4 py-2.5 rounded-full hover:bg-[#F5A623]/10 transition-colors"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F5A623] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#F5A623]"></span>
              </span>
              {CONTACT.phone}
            </a>
          </nav>

          <button
            data-testid="header-mobile-menu-button"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0C1827]/95 backdrop-blur-xl border-t border-[#1E324A] px-6 py-5 space-y-4">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="block text-base font-medium text-slate-200 hover:text-[#F5A623]"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#quote"
            onClick={() => setOpen(false)}
            className="block text-base font-semibold text-[#F5A623]"
          >
            Get a Quote
          </a>
          <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-base font-semibold text-white">
            <Phone size={18} className="text-[#F5A623]" /> {CONTACT.phone}
          </a>
        </div>
      )}
    </header>
  );
};
